<script lang="ts">
import InstantWinCountdownBanner from '@/components/banners/InstantWinCountdownBanner.vue'
import AppIcon from '@/components/icons/AppIcon.vue'
import BalanceBreakdownModalContent from '@/components/modals/BalanceBreakdownModalContent.vue'
import { useCheckoutBasket } from '@/plugins/useCheckoutBasket'
import { useCredit } from '@/plugins/useCredit'
import { usePaymentProvider } from '@/plugins/usePaymentProvider'
import { AnalyticsService } from '@/services/analytics'
import { InstantWinStorage } from '@/services/instant-win-storage'
import { useFeatureFlagsStore } from '@/stores/feature-flags'
import type {
  BasketItem,
  CheckoutContext,
  InstantWinDraw,
  Nullable,
  OrderSummaryItem,
  Plan,
  Product,
  ReservationData,
  User,
} from '@/types'
import {
  calculateChargeableAmount,
  calculateDueAmount,
  calculateUsedCredit,
  checkAndCreateReservation,
  createPaymentError,
  handleReservationExpiry,
  isCreditOnlyPayment,
  navigateAfterPayment,
  processGameSession,
  shouldShowAddressFields,
  shouldShowPersonalFields,
  validatePaymentForm,
} from '@/utils/checkout-helpers'
import { toPeriod } from '@/utils/date-time-utils'
import {
  CountryOptions,
  Icons,
  PaymentProviders,
  ProductTypes,
} from '@/utils/helper-objects'
import { isE164PhoneNumber, isPostcode } from '@/utils/input-validator-utils'
import { isInstantWinDraw, isPlan } from '@/utils/type-guard-utils'
import { formatToPhoneNumber } from '@/utils/utils'
import type { PaymentError } from '@/validation-exception'
import PaymentStageSolidgate from '@/views/Checkout/CheckoutStages/PaymentStageSolidgate.vue'
import { type PropType, defineComponent, ref, useTemplateRef } from 'vue'

export default defineComponent({
  components: {
    InstantWinCountdownBanner,
    AppIcon,
    BalanceBreakdownModalContent,
    PaymentStageSolidgate,
  },
  props: {
    checkoutContext: {
      type: Object as PropType<CheckoutContext>,
      required: true,
    },
    user: {
      type: Object as PropType<User>,
      required: true,
    },
  },
  emits: ['reservationExpired', 'paymentProcessingUpdate'],
  setup(props) {
    const featureFlagsStore = useFeatureFlagsStore()
    const { refreshCreditBalance } = useCheckoutBasket()
    const fieldsToOmit = [
      'credit',
      'loyalty',
      'subscriptions',
      'tickets',
      'tier',
      'user_progression',
    ]
    const formData = Object.fromEntries(
      Object.entries(props.user).filter(([key]) => !fieldsToOmit.includes(key))
    )

    // Ensure all form fields have proper default values
    formData.consent = true
    formData.contact_optin = formData.contact_optin ?? true
    formData.address_line_1 = formData.address_line_1 || ''
    formData.address_line_2 = formData.address_line_2 || ''
    formData.city = formData.city || ''
    formData.post_code = formData.post_code || ''
    formData.country = formData.country || CountryOptions.UnitedKingdom

    // Clear invalid phone number to prevent form validation issues
    if (
      formData.phone &&
      isE164PhoneNumber({
        name: 'phone',
        value: String(formData.phone),
      }) !== true
    ) {
      formData.phone = ''
    }

    // Clear invalid postcode to prevent form validation issues
    if (
      formData.post_code &&
      isPostcode({
        name: 'post_code',
        value: String(formData.post_code),
      }) !== true
    ) {
      formData.post_code = ''
    }

    const { chargeCredit } = useCredit()

    const isPaymentProcessing = ref(false)

    // Form validity should start as false and be determined by actual validation
    // Don't assume logged-in users have complete data
    const isFormValid = ref(false)

    // New checkout flow always uses Solidgate
    const paymentProvider = PaymentProviders.Solidgate

    const {
      provider,
      mount,
      handlePaymentAuthorized,
      handlePaymentError,
      errors: paymentErrors,
    } = usePaymentProvider(paymentProvider)

    return {
      isFeatureEnabled: (flag: string) =>
        featureFlagsStore.isFeatureEnabled(flag),
      formData,
      paymentErrors,
      paymentStageRef:
        useTemplateRef<InstanceType<typeof PaymentStageSolidgate>>(
          'paymentStageRef'
        ),
      isPaymentProcessing,
      provider,
      mount,
      handlePaymentAuthorized,
      handlePaymentError,
      chargeCredit,
      isFormValid,
      paymentProvider,
      isPostcode,
      isInstantWinDraw,
      isPlan,
      isE164PhoneNumber,
      formatToPhoneNumber,
      toPeriod,
      CountryOptions,
      Icons,
      refreshCreditBalance,
    }
  },
  data() {
    return {
      form: {
        isLoading: false,
        errors: null as Nullable<PaymentError>,
        validators: {},
      },
      reservationData: null as Nullable<ReservationData>,
      isPaymentReady: false,
      useCredit: false,
      reservationLoading: false,
      userProgression: {
        xpEarnable: 0,
        xpEarnableFromPurchase: 0,
        xpEarnableFromCashable: 0,
        cashbackDiscountAmount: 0,
      },
    }
  },
  computed: {
    chargeableAmount(): number {
      return calculateChargeableAmount(this.checkoutContext)
    },

    availableUserCredit(): number {
      return Number(((this.user?.credit.totals.overall ?? 0) / 100).toFixed(2))
    },

    usedCredit(): number {
      return calculateUsedCredit(
        this.useCredit,
        this.chargeableAmount,
        this.availableUserCredit
      )
    },

    paymentDue(): number {
      return calculateDueAmount(
        this.checkoutContext,
        this.chargeableAmount,
        this.usedCredit
      )
    },

    loyaltyCredits(): number {
      return Number(((this.user?.loyalty.total.credits ?? 0) / 100).toFixed(2))
    },

    userWinnings(): number {
      return Number(((this.user?.credit.totals.cashable ?? 0) / 100).toFixed(2))
    },

    primaryProduct(): Nullable<Product> {
      return this.checkoutContext.basket.items[0]?.product || null
    },

    primaryProductQuantity(): number {
      return this.checkoutContext.basket.items[0]?.quantity || 1
    },

    PaymentComponent() {
      return PaymentStageSolidgate
    },

    shouldShowCountdown(): boolean {
      return this.checkoutContext.basket.items.some(
        (item) => item.type === ProductTypes.InstantWinDraw
      )
    },

    isCreditOnlyPayment(): boolean {
      return isCreditOnlyPayment(
        this.useCredit,
        this.paymentDue,
        this.checkoutContext
      )
    },

    shouldShowAddressFields(): boolean {
      return shouldShowAddressFields(
        this.user,
        this.paymentDue,
        this.checkoutContext,
        this.isFeatureEnabled,
        this.isPostcode
      )
    },

    shouldShowPersonalFields(): boolean {
      return shouldShowPersonalFields(this.user)
    },

    formValidators() {
      const validators: any = {
        phone: [isE164PhoneNumber],
      }

      // Only add postcode validation if address fields are shown
      if (this.shouldShowAddressFields) {
        validators.post_code = [isPostcode]
      }

      return validators
    },

    isUserPhoneValid(): boolean {
      if (!this.user.phone || String(this.user.phone).trim() === '') {
        return false
      }
      return (
        this.isE164PhoneNumber({
          name: 'phone',
          value: String(this.user.phone),
        }) === true
      )
    },

    /**
     * Returns structured order summary data based on product type
     */
    orderSummaryData(): { items: OrderSummaryItem[] } {
      if (this.isInstantWinDraw(this.primaryProduct)) {
        // Instant win summary structure
        return {
          items: [
            {
              label: this.primaryProduct.title,
              detail: ` (${this.$t(
                'label.entries',
                this.primaryProductQuantity,
                {
                  named: {
                    n: this.$n(this.primaryProductQuantity, 'number'),
                  },
                }
              )})`,
              amount: this.chargeableAmount,
            },
          ],
        }
      }

      const plan = this.primaryProduct as Plan

      if (plan.temp_discount === true) {
        const items: OrderSummaryItem[] = []
        const discountPercent =
          plan.cost > 0
            ? Math.round(((plan.cost - plan.discount_cost) / plan.cost) * 100)
            : 0

        if (plan.has_free_trial) {
          items.push({
            label: 'Today',
            amount: 0,
            detail: '7-day free trial',
          })
        }

        items.push({
          label: plan.has_free_trial
            ? 'After 7-day trial'
            : 'Limited time price',
          amount: plan.discount_cost,
          detail:
            discountPercent > 0
              ? `${discountPercent}% off first ${toPeriod(plan.period, 'full')}`
              : undefined,
          highlight: true,
        })

        items.push({
          label: 'Standard price',
          amount: plan.cost,
          detail: `${plan.period}`,
          isOriginal: true,
        })

        return { items }
      }

      // Standard plan summary (keep existing simple structure)
      return {
        items: [
          {
            label: 'Total',
            amount: this.chargeableAmount,
            trialDetail: plan.has_free_trial ? '7 Day Free Trial' : undefined,
          },
        ],
      }
    },

    /**
     * Show detailed breakdown for temp discount plans
     */
    showDetailedBreakdown(): boolean {
      if (this.isInstantWinDraw(this.primaryProduct)) {
        return false
      }
      const plan = this.primaryProduct as Plan
      return Boolean(plan.temp_discount)
    },

    /**
     * Combined loading state for the purchase button
     * Excludes form submission state which is in template scope
     */
    isButtonLoading(): boolean {
      return (
        this.form.isLoading ||
        this.isPaymentProcessing ||
        this.reservationLoading
      )
    },

    /**
     * Combined disabled state for the purchase button
     * Excludes form submission state which is in template scope
     */
    isButtonDisabled(): boolean {
      return this.isButtonLoading
    },
  },
  watch: {
    useCredit() {
      // Only trigger update if payment component is ready
      if (
        this.isPaymentReady &&
        this.paymentStageRef &&
        this.paymentDue !== 0
      ) {
        this.paymentStageRef.updateCreditAmount(this.usedCredit)
      }
    },
    isPaymentProcessing(newValue: boolean) {
      this.$emit('paymentProcessingUpdate', newValue)
    },
  },
  async created() {
    // Initialize credit toggle early, before payment component mounts
    if (this.availableUserCredit > 0) {
      const hasInstantWin = this.checkoutContext.basket.items.some(
        (item) => item.type === ProductTypes.InstantWinDraw
      )

      // Auto-enable credit only for instant wins
      if (hasInstantWin) {
        this.useCredit = true
      }
    }
  },

  async mounted() {
    this.reservationLoading = true
    const reservationSuccess = await this.checkReservationExpiry()

    // Only clear loading if reservation was successful
    // If it failed, keep button disabled so user can't submit form
    if (reservationSuccess) {
      this.reservationLoading = false
    }

    // Let the form validation system determine the initial state
    // Don't assume logged-in users have complete data
    setTimeout(() => {
      if (this.paymentStageRef?.updateAPMButtonState) {
        this.paymentStageRef.updateAPMButtonState(this.isFormValid)
      }
    }, 300)

    this.checkoutContext.basket.items.forEach((item: BasketItem) => {
      if (item.type === ProductTypes.InstantWinDraw) {
        AnalyticsService.trackBeginGameCheckout(item)
      } else {
        AnalyticsService.trackBeginSubscriptionCheckout(item)
      }
    })
  },
  async beforeMount() {
    try {
      // Initialize the selected payment provider
      const productType = this.checkoutContext.basket.items.some(
        (item) => item.type === ProductTypes.Subscription
      )
        ? ProductTypes.Subscription
        : ProductTypes.InstantWinDraw

      await this.mount(productType)
      this.isPaymentReady = true
    } catch (error) {
      console.error(`Failed to initialize ${this.paymentProvider}:`, error)
      this.isPaymentReady = false
    }
  },
  methods: {
    async checkReservationExpiry(): Promise<boolean> {
      const result = await checkAndCreateReservation(this.checkoutContext)

      if (!result.success) {
        // Emit event to close the checkout modal before showing error alert
        this.$emit('reservationExpired')
        this.$alertStore.addErrorAlert({
          message:
            result.error || 'Unable to reserve tickets. Please try again.',
        })
        void this.$router.push({ name: 'instant-wins' })
        return false
      }

      // Store reservation data if available
      if (result.reservationData) {
        this.reservationData = result.reservationData
      }

      return true
    },

    handleReservationExpiry() {
      const result = handleReservationExpiry()

      if (result.shouldRedirect) {
        this.$emit('reservationExpired')
      }
    },

    goToFirstField(firstInvalid: boolean = false) {
      void this.$nextTick(() => {
        const firstInvalidField = document.querySelector(
          `input[aria-invalid="${firstInvalid ? 'true' : 'false'}"]`
        ) as Nullable<HTMLInputElement>

        if (firstInvalidField) {
          firstInvalidField.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          })
          firstInvalidField.focus()
        }
      })
    },
    async submit(isValid: boolean) {
      if (this.form.isLoading) {
        return
      }

      if (!validatePaymentForm(isValid, this.isCreditOnlyPayment)) {
        this.goToFirstField(true)
        return
      }

      // Reset errors and set loading state
      this.form.errors = null
      this.form.isLoading = true

      try {
        if (this.isCreditOnlyPayment) {
          // Handle credit-only payments for instant wins
          this.isPaymentProcessing = true

          const instantWinItems = this.checkoutContext.basket.items.filter(
            (item) => item.type === ProductTypes.InstantWinDraw
          )
          const instantWin = instantWinItems[0].product as InstantWinDraw
          const quantity = instantWinItems[0].quantity

          const creditChargedata = await this.chargeCredit(
            instantWin.code,
            quantity,
            InstantWinStorage.getStoredReservation(instantWin)?.lock_key
          )

          if (creditChargedata.error) {
            this.form.errors = { message: creditChargedata.error }
            this.form.isLoading = false
            this.isPaymentProcessing = false
            return
          }

          // Clear reservation after successful credit payment
          InstantWinStorage.removeReservation(instantWin)

          await this.handlePaymentSuccess(creditChargedata.csi_content)
          return
        } else {
          // Process payment via the payment provider component
          const result = await this.paymentStageRef?.processPayment()

          // Reset button loading state after processPayment returns
          // Modal visibility is controlled by isPaymentProcessing only
          this.form.isLoading = false

          if (result?.timedOut && !result.success) {
            const errorMessage =
              result.data.error ||
              result.data.errorMessage ||
              'Payment processing timed out. Please contact customer support.'

            this.form.errors = createPaymentError(errorMessage, result.orderId)
            this.isPaymentProcessing = false
            return
          }
        }
      } catch (error: any) {
        this.form.isLoading = false
        this.isPaymentProcessing = false
        this.form.errors = { message: error.message }
      }
    },
    async handlePaymentResult(payment: any) {
      if (payment && payment.success) {
        await this.handlePaymentSuccess(payment)
      } else {
        this.form.isLoading = false
        this.isPaymentProcessing = false
        if (payment.data?.error) {
          this.form.errors = createPaymentError(
            payment.data.error,
            payment.orderId
          )
        }
      }
    },

    async handlePaymentSuccess(payment: any) {
      if (!payment.data) {
        const errorMessage =
          "Failed to retrieve payment information. The payment was processed but we couldn't confirm your purchase."

        this.form.isLoading = false
        this.isPaymentProcessing = false
        this.form.errors = createPaymentError(errorMessage, payment.orderId)

        return
      }

      // Process game session data for instant wins
      let gameSessionData // Will be undefined for subscriptions
      if (
        this.checkoutContext.basket.items.some(
          (item) => item.type === ProductTypes.InstantWinDraw
        )
      ) {
        const gameSessionResult = await processGameSession(
          payment,
          this.checkoutContext,
          this.$userStore
        )

        if (!gameSessionResult.success) {
          this.isPaymentProcessing = false
          this.form.isLoading = false
          this.form.errors = createPaymentError(
            gameSessionResult.error!,
            payment.orderId
          )
          return
        }

        gameSessionData = gameSessionResult.gameSessionData
      }

      // If a subscription was purchased, force-refresh user details so UI reflects new tier immediately
      const subscriptionItem = this.checkoutContext.basket.items.find(
        (item) => item.type === ProductTypes.Subscription
      )
      const hasSubscription = Boolean(subscriptionItem)
      const targetPlanCode =
        (subscriptionItem?.product as Plan | undefined)?.code ?? null

      if (hasSubscription) {
        try {
          await this.$userStore.waitForPlan(targetPlanCode)

          const planName =
            (subscriptionItem?.product as Plan | undefined)?.name ??
            'your subscription'

          this.$alertStore.addSuccessAlert({
            title: 'Subscription updated',
            message: `You're now on ${planName}.`,
            buttonText: 'Continue',
          })
        } catch (error) {
          console.error(
            'Failed to refresh user details after subscription purchase:',
            error
          )
          this.$alertStore.addErrorAlert({
            message:
              'Your subscription is processing. We will refresh your account shortly.',
          })

          // Non-fatal: navigation still proceeds even if refresh fails
        }
      }

      // Navigate after payment (handles both instant wins and subscriptions)
      navigateAfterPayment(
        payment,
        this.checkoutContext,
        this.$router,
        gameSessionData,
        this.refreshCreditBalance
      )
    },

    handleFormValidationChange(isValid: boolean) {
      this.isFormValid = isValid

      if (this.paymentStageRef?.updateAPMButtonState) {
        this.paymentStageRef.updateAPMButtonState(isValid)
      }
    },

    /**
     * Handle when a payment intent is updated to update the user progression.
     */
    handlePaymentIntentUpdated(paymentIntent: any) {
      this.userProgression.xpEarnable =
        paymentIntent.user_progression?.xp_earnable || 0
      this.userProgression.xpEarnableFromPurchase =
        paymentIntent.user_progression?.xp_earnable_from_purchase || 0
      this.userProgression.xpEarnableFromCashable =
        paymentIntent.user_progression?.xp_earnable_from_credit || 0
      this.userProgression.cashbackDiscountAmount =
        paymentIntent.user_progression?.xp_cashback_amount || 0
    },

    /**
     * Format phone number to E.164 format with +44 prefix
     */
    formatPhoneNumber() {
      this.formData.phone = this.formatToPhoneNumber(
        String(this.formData.phone)
      )
    },
  },
})
</script>

<template>
  <div
    class="flex w-full flex-col items-center justify-center"
    :class="isPaymentProcessing ? '' : 'hidden'"
    v-bind="$attrs"
  >
    <div class="spinner-2 size-12"></div>
    <div class="mt-8 text-center text-2xl font-bold">
      Your order is being processed...
      <p class="text-sm text-gray-500">
        Please do not refresh or close this page.
      </p>
    </div>
  </div>
  <div
    class="flex w-full flex-col"
    :class="isPaymentProcessing ? 'hidden' : ''"
    v-bind="$attrs"
  >
    <div class="mb-4 text-center text-xl font-bold">Checkout</div>

    <!-- Show countdown banner for instant win products -->
    <InstantWinCountdownBanner
      v-if="
        shouldShowCountdown &&
        reservationData &&
        primaryProduct &&
        isInstantWinDraw(primaryProduct)
      "
      :reservation-data="reservationData"
      :instant-win="primaryProduct as InstantWinDraw"
      :emit-on-expiry="true"
      banner-class="z-10 !p-0"
      countdown-class="rounded-t-lg"
      @countdown-finished="handleReservationExpiry"
    />

    <!-- Order Summary -->
    <div
      v-if="primaryProduct"
      class="flex flex-col border border-slate-300 bg-pure-white p-6"
      :class="{
        'rounded-lg rounded-t-lg': !shouldShowCountdown,
        'rounded-b-lg': shouldShowCountdown,
      }"
    >
      <!-- Balance Section - Only show for instant wins with credits enabled -->
      <div
        v-if="isInstantWinDraw(primaryProduct) && availableUserCredit > 0"
        class="mb-2 flex justify-between border-b pb-4"
      >
        <div class="flex cursor-pointer items-center">
          <div>Balance</div>
          <div class="mb-1">
            <AppInformationModal
              id="balance-breakdown"
              icon-class="mx-1 mt-1 text-blue-600"
              modal-class="w-[365px] bg-pure-white p-4"
              default-slot-class="w-full"
              :show-faq-button="false"
            >
              <BalanceBreakdownModalContent
                :available-user-credit="availableUserCredit"
                :user-winnings="userWinnings"
                :loyalty-credits="loyaltyCredits"
              />
            </AppInformationModal>
          </div>
          <div class="font-bold">
            <span class="font-normal">:</span>
            {{ $n(user.credit.totals.overall / 100, 'currency') }}
          </div>
        </div>

        <AppToggle
          id="use-credit-checkbox"
          v-model="useCredit"
          name="use-credit-checkbox"
          :disabled="
            !(availableUserCredit > 0) || form.isLoading || isPaymentProcessing
          "
        />
      </div>

      <!-- Plan Name Section - Only for subscriptions -->
      <div
        v-if="primaryProduct && isPlan(primaryProduct)"
        class="border-b pb-4"
      >
        <div class="text-xl">
          <span class="font-normal">Plan: </span>
          <span class="font-bold">{{ primaryProduct.name }}</span>
          <span class="font-normal"> ({{ primaryProduct.period }})</span>
        </div>
      </div>

      <!-- Order Summary Title -->
      <div class="pt-2 font-bold">Order Summary</div>

      <!-- Dynamic Order Summary based on plan type -->
      <template v-if="showDetailedBreakdown">
        <!-- Temp Discount Plan: Detailed breakdown -->
        <div
          v-for="(item, index) in orderSummaryData.items"
          :key="index"
          class="flex items-center justify-between pt-2"
        >
          <div>
            <span class="font-medium">{{ item.label }}</span>
            <span v-if="item.detail" class="ml-1 text-sm text-gray-600">
              ({{ item.detail }})
            </span>
          </div>
          <div
            class="text-right"
            :class="[
              item.isOriginal
                ? 'font-semibold text-[#5F7D92] line-through'
                : 'font-bold',
              item.highlight ? 'text-emerald-700' : '',
            ]"
          >
            {{
              $n(item.amount, 'currency', {
                minimumFractionDigits: 2,
              })
            }}
          </div>
        </div>
      </template>

      <template v-else>
        <!-- Standard/Instant Win: Simple structure -->
        <div
          v-for="(item, index) in orderSummaryData.items"
          :key="index"
          class="flex items-center justify-between pt-2"
        >
          <div>
            <span v-if="item.label">{{ item.label }}</span>
            <span v-if="'detail' in item && item.detail" class="text-sm">
              {{ item.detail }}</span
            >
          </div>
          <div class="text-right font-bold">
            {{
              $n(item.amount, 'currency', {
                minimumFractionDigits: 2,
              })
            }}
            <span
              v-if="'trialDetail' in item && item.trialDetail"
              class="ml-1 text-sm font-normal text-gray-600"
            >
              ({{ item.trialDetail }})
            </span>
          </div>
        </div>
      </template>

      <!-- Used Credit - Only for instant wins with credits -->
      <div
        v-if="isInstantWinDraw(primaryProduct) && usedCredit > 0"
        class="flex items-center justify-between pt-2"
      >
        <div>Balance</div>
        <div class="text-right font-bold">
          -{{
            $n(usedCredit, 'currency', {
              minimumFractionDigits: 2,
            })
          }}
        </div>
      </div>

      <!-- Due Amount - Only show for non-temp-discount plans -->
      <div
        v-if="!showDetailedBreakdown"
        class="flex items-center justify-between pt-2"
      >
        <div>Due</div>
        <div class="text-right">
          <div class="font-bold">
            {{
              $n(paymentDue, 'currency', {
                minimumFractionDigits: 2,
              })
            }}
          </div>
          <div
            v-if="isPlan(primaryProduct) && primaryProduct.has_free_trial"
            class="text-xs text-gray-600"
          >
            ({{
              $n(primaryProduct.discount_cost, 'currency', {
                minimumFractionDigits: 2,
              })
            }}
            / {{ primaryProduct.period }} after)
          </div>
        </div>
      </div>

      <!-- XP Earnings -->
      <div
        v-if="!isCreditOnlyPayment && userProgression.xpEarnable > 0"
        class="flex items-center justify-between pt-2"
      >
        <div>This purchase will earn you:</div>
        <div class="text-right font-bold text-primary">
          {{
            $n(userProgression.xpEarnable, 'number', {
              minimumFractionDigits: 0,
            })
          }}XP
        </div>
      </div>

      <!-- Cashback -->
      <div
        v-if="
          isInstantWinDraw(primaryProduct) &&
          !isCreditOnlyPayment &&
          userProgression.cashbackDiscountAmount > 0
        "
        class="flex items-center justify-between pt-2"
      >
        <div>Cashback to be received:</div>
        <div class="text-right font-bold text-primary">
          {{ $n(userProgression.cashbackDiscountAmount / 100, 'currency') }}
        </div>
      </div>
    </div>

    <AppForm
      :data="formData"
      :errors="form.errors"
      :validators="formValidators"
      :validate-on-input="true"
      class="mt-3 flex flex-col space-y-3"
      :show-required-note="false"
      :show-complete-errors="false"
      @submit="submit"
      @validation-changed="handleFormValidationChange"
    >
      <template #default="{ errors }">
        <!-- Error Display -->
        <div
          v-if="
            form.errors &&
            paymentDue === 0 &&
            (!primaryProduct || isInstantWinDraw(primaryProduct)) &&
            !isPaymentProcessing
          "
          class="mb-4 rounded-lg border-l-4 border-red-500 bg-red-50 p-4"
        >
          <div class="flex items-start justify-between">
            <div class="flex items-start">
              <AppIcon
                :icon="Icons.Error"
                class="mr-3 mt-1 text-red-500"
                size="sm"
              />
              <div>
                <h3 class="text-sm font-medium text-red-800">Payment Error</h3>
                <p class="mt-1 text-sm text-red-700">
                  {{ form.errors.message }}
                </p>
              </div>
            </div>
            <button
              class="ml-4 inline-flex rounded-md bg-red-50 p-1.5 text-red-400 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
              @click="form.errors = null"
            >
              <AppIcon :icon="Icons.Times" size="xs" />
            </button>
          </div>
        </div>

        <div
          :class="
            isPaymentProcessing ||
            (paymentDue === 0 &&
              (!primaryProduct || isInstantWinDraw(primaryProduct)))
              ? 'hidden'
              : ''
          "
          class="space-y-3"
        >
          <!-- Personal Fields - Show when missing from user account -->
          <div v-if="shouldShowPersonalFields">
            <div
              class="flex flex-col rounded-lg border border-slate-300 bg-pure-white p-6"
            >
              <div class="space-y-4">
                <div>
                  <AppInput
                    v-if="
                      !user.first_name || String(user.first_name).trim() === ''
                    "
                    v-model="formData.first_name"
                    name="first_name"
                    placeholder="First Name"
                    label="First Name"
                    autocomplete="given-name"
                    :use-floating-placeholder="false"
                    :required="true"
                    :errors
                  />
                </div>
                <div>
                  <AppInput
                    v-if="
                      !user.last_name || String(user.last_name).trim() === ''
                    "
                    v-model="formData.last_name"
                    name="last_name"
                    placeholder="Last Name"
                    label="Last Name"
                    autocomplete="family-name"
                    :use-floating-placeholder="false"
                    :required="true"
                    :errors
                  />
                </div>
                <div>
                  <AppInput
                    v-if="!isUserPhoneValid"
                    v-model="formData.phone"
                    name="phone"
                    placeholder="Phone"
                    label="Phone"
                    autocomplete="tel"
                    type="tel"
                    :use-floating-placeholder="false"
                    :required="true"
                    :errors
                    @change="formatPhoneNumber"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Address Fields - Conditional based on feature flags and payment amount -->
          <div v-if="shouldShowAddressFields">
            <div
              class="flex flex-col rounded-lg border border-slate-300 bg-pure-white p-6"
            >
              <div class="space-y-4">
                <div>
                  <AppInput
                    v-if="!user.address_line_1"
                    v-model="formData.address_line_1"
                    name="address_line_1"
                    placeholder="Address 1"
                    label="Address 1"
                    autocomplete="address-line1"
                    :use-floating-placeholder="false"
                    :required="shouldShowAddressFields"
                    :errors
                  />
                </div>
                <div>
                  <AppInput
                    v-if="!user.city"
                    v-model="formData.city"
                    name="city"
                    placeholder="City"
                    label="City"
                    autocomplete="address-level2"
                    :use-floating-placeholder="false"
                    :required="shouldShowAddressFields"
                    :errors
                  />
                </div>
                <div>
                  <AppInput
                    v-if="
                      !user.post_code ||
                      !isPostcode({
                        name: 'post_code',
                        value: String(formData.post_code),
                      })
                    "
                    v-model="formData.post_code"
                    name="post_code"
                    placeholder="Postcode"
                    label="Postcode"
                    autocomplete="postal-code"
                    :use-floating-placeholder="false"
                    :required="shouldShowAddressFields"
                    :errors
                  />
                </div>
                <div>
                  <AppSelect
                    v-if="!user.country"
                    v-model="formData.country"
                    name="country"
                    placeholder="Country"
                    autocomplete="country"
                    :options="CountryOptions"
                    :required="shouldShowAddressFields"
                    :errors
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Payment Component -->
          <div>
            <div
              class="flex flex-col rounded-lg border border-slate-300 bg-pure-white p-6"
            >
              <component
                :is="PaymentComponent"
                v-if="
                  isPaymentReady &&
                  (!shouldShowCountdown || reservationData?.lock_key)
                "
                ref="paymentStageRef"
                v-model="formData"
                :errors="form.errors?.message || null"
                :payment-errors="paymentErrors"
                :product="primaryProduct"
                :ticket-count="primaryProductQuantity"
                :used-credit="usedCredit"
                :form-valid="isFormValid"
                @payment-intent:updated="handlePaymentIntentUpdated"
                @payment-authorized="handlePaymentResult"
                @payment-error="isPaymentProcessing = false"
                @payment-processing="isPaymentProcessing = $event"
              >
                <template #optin>
                  <p class="mt-6 text-sm">
                    Your personal data will be used to process your order,
                    support your experience throughout this website, and for
                    other purposes described in our
                    <RouterLink :to="{ name: 'privacy-policy' }"
                      >privacy policy</RouterLink
                    >.
                  </p>
                  <AppCheckbox
                    v-model="formData.consent"
                    name="consent"
                    container-class="text-sm mt-4"
                    :label="
                      primaryProduct && isInstantWinDraw(primaryProduct)
                        ? $t(
                            'section.instant_wins.form.checkbox.consent_accept',
                            {
                              url: $router.resolve({
                                name: 'instant-win-terms-and-conditions',
                                params: { instantWinCode: primaryProduct.code },
                              }).href,
                            }
                          )
                        : $t('form.checkbox.consent_accept')
                    "
                    :required="true"
                    :errors
                  />

                  <!-- Marketing communications checkbox - only for subscriptions -->
                  <AppCheckbox
                    v-if="primaryProduct && !isInstantWinDraw(primaryProduct)"
                    v-model="formData.contact_optin"
                    name="contact_optin"
                    container-class="text-sm"
                    :label="$t('form.checkbox.marketing_comms_accept')"
                    :errors
                  />
                </template>
              </component>
            </div>
          </div>
        </div>
      </template>

      <template #buttons="{ isSubmitting }">
        <AppButton
          class="button-primary mx-auto my-5 w-full"
          title="Purchase"
          :is-loading="isButtonLoading || isSubmitting"
          :disabled="isButtonDisabled || isSubmitting"
          type="submit"
          :icon="Icons.ArrowRight"
          data-cy="purchase-button"
        >
          Purchase
        </AppButton>
      </template>
    </AppForm>
  </div>
</template>

<style lang="scss">
#use-credit-checkbox input[type='checkbox'] {
  &:checked {
    @apply bg-orange-500;
  }
}
</style>
